import { useEffect, useRef, useState } from 'react';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { textOnImageBody, textOnImageHeading } from '../theme/text-on-image';
import type { DaySummary } from '../lib/api/content-api-client';

interface DayCardProps {
  readonly summary: DaySummary;
}

/** Slideshow tick (ms) — how long each image stays visible while hovered. */
const SLIDESHOW_INTERVAL_MS = 3000;

/** Crossfade duration (ms) — must be < SLIDESHOW_INTERVAL_MS so the fade settles. */
const CROSSFADE_MS = 1000;

/**
 * `<DayCard>` — bespoke row card used on the Days listing per
 * Requirement 3.3.
 *
 * Layout invariants (round-13 refresh):
 *   - The title (the date, with the year only when not the current
 *     year — the upstream `summary.title` is already rendered via the
 *     smart-year formatter) is **pinned to the top-left** of the card.
 *   - The body shows only the day's summary (`hookPhrase`).
 *   - The cover image takes the **right 50%** of the card with a
 *     **16:9 aspect ratio** — the card's overall height follows the
 *     image's natural height at that width (`cardWidth × 0.5 ÷ 16 × 9`).
 *   - The image's left edge fades to the card's surface color via a
 *     gradient overlay so the image bleeds smoothly into the text
 *     column rather than presenting a hard vertical line. Uses MUI's
 *     `alpha()` helper to produce a valid rgba transparent stop
 *     regardless of the resolved theme color format.
 *   - On hover, the image cycles through every URL in
 *     `summary.images` (the per-day slideshow) with a real opacity
 *     crossfade between adjacent images. Each image is rendered as
 *     its own absolutely-positioned layer so transitions actually
 *     animate (a single `<img>` whose `src` changes wouldn't).
 *     `mouseleave` snaps back to the primary image.
 *
 * The whole card is a `<CardActionArea>` linking to
 * `/daily/{summary.date}` so a click anywhere routes the visitor to
 * the per-day detail page.
 */
export function DayCard({ summary }: DayCardProps): JSX.Element {
  const images = summary.images.length > 0
    ? summary.images
    : summary.primaryImage
      ? [summary.primaryImage]
      : [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovering, setHovering] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const initialTimeoutRef = useRef<number | null>(null);
  // Drive the slideshow while the card is hovered AND there is more
  // than one image to cycle through. The first swap fires after
  // `SLIDESHOW_INTERVAL_MS / 2` so the hover responds quickly,
  // subsequent swaps tick at the full interval.
  useEffect(() => {
    if (!hovering || images.length <= 1) return;
    initialTimeoutRef.current = window.setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % images.length);
      intervalRef.current = window.setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % images.length);
      }, SLIDESHOW_INTERVAL_MS);
    }, SLIDESHOW_INTERVAL_MS / 2);
    return () => {
      if (initialTimeoutRef.current !== null) {
        window.clearTimeout(initialTimeoutRef.current);
        initialTimeoutRef.current = null;
      }
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [hovering, images.length]);
  return (
    <Card
      variant="outlined"
      sx={{ overflow: 'hidden' }}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => {
        setHovering(false);
        setActiveIndex(0);
      }}
    >
      <CardActionArea
        href={`/daily/${summary.date}`}
        sx={{ display: 'flex', alignItems: 'stretch', position: 'relative', overflow: 'hidden' }}
      >
        {/* z-0: card-wide background image, 70% opacity, scaled
            1.5× to fit. Each image is its own absolute layer; only
            the active layer is visible at any moment. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
          }}
        >
          {images.map((url, index) => (
            <Box
              key={`bg-${url}`}
              component="img"
              src={url}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scale(1.5)',
                transformOrigin: 'left center',
                opacity: index === activeIndex ? 0.5 : 0,
                transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
              }}
            />
          ))}
        </Box>
        {/* z-3: top-down dark gradient running the full width of
            the card. Sits over the background image (and the
            right-half focused image, which is masked-faded into the
            bg) to give the title + body extra contrast. The dark
            band reaches further down on the title-anchored top
            edge, then fades to neutral by 80% so the bottom of the
            card stays unobscured. */}
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 3,
            pointerEvents: 'none',
            background:
              'linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.2) 50%, rgba(0, 0, 0, 0) 80%)',
          }}
        />
        <Stack
          spacing={1}
          sx={{
            position: 'relative',
            zIndex: 4,
            width: '35%',
            flexShrink: 0,
            p: { xs: 2, sm: 2.5 },
            justifyContent: 'flex-start',
            minWidth: 0,
          }}
        >
          <Typography
            variant="h3"
            component="h3"
            sx={{
              ...textOnImageHeading,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
            }}
          >
            {summary.title}
          </Typography>
          {summary.hookPhrase && (
            <Typography
              variant="body2"
              sx={{
                ...textOnImageBody,
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 5,
                overflow: 'hidden',
              }}
            >
              {summary.hookPhrase}
            </Typography>
          )}
        </Stack>
        <Box
          sx={{
            width: '65%',
            flexShrink: 0,
            aspectRatio: '16 / 9',
            position: 'relative',
            zIndex: 1,
            overflow: 'hidden',
          }}
        >
          {images.map((url, index) => (
            <Box
              key={url}
              component="img"
              src={url}
              alt={index === 0 ? summary.title : ''}
              aria-hidden={index === activeIndex ? undefined : 'true'}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: index === activeIndex ? 1 : 0,
                transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
                // Native CSS mask: fades the image to transparent
                // along its left edge so it blends into the
                // background-image layer behind it (rather than
                // sitting on top of a surface-color overlay).
                // WebKit prefix retains support for older Safari.
                maskImage:
                  'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 1) 25%)',
                WebkitMaskImage:
                  'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 1) 25%)',
              }}
            />
          ))}
        </Box>
      </CardActionArea>
    </Card>
  );
}
